import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useCurrentUser } from './shared/hooks/useCurrentUser.js';
import { AppShell } from './shared/components/AppShell.jsx';
import { Login } from './pages/Login.jsx';
import { Settings } from './pages/Settings.jsx';
import logo from './shared/assets/sincro-logo.png';

// Lazy-load each module's route tree. Chart.js + module-specific code then
// ship as separate chunks — users who never visit Operations skip the
// ~100KB chart.js payload; users on Operations skip the Admin module, etc.
// Named exports need a `.then(m => ({default: m.X}))` shim since React.lazy
// only understands default exports.
const AdminRoutes = lazy(() => import('./modules/admin/AdminRoutes.jsx')
  .then((m) => ({ default: m.AdminRoutes })));
const OperationsRoutes = lazy(() => import('./modules/operations/OperationsRoutes.jsx')
  .then((m) => ({ default: m.OperationsRoutes })));
const WorkforceRoutes = lazy(() => import('./modules/workforce/WorkforceRoutes.jsx')
  .then((m) => ({ default: m.WorkforceRoutes })));
const AnalyticsRoutes = lazy(() => import('./modules/analytics/AnalyticsRoutes.jsx')
  .then((m) => ({ default: m.AnalyticsRoutes })));
const ClientRoutes = lazy(() => import('./modules/client/ClientRoutes.jsx')
  .then((m) => ({ default: m.ClientRoutes })));
// Kiosk page is lazy AND rendered outside AppShell/auth wrapper — no OAuth
// session. Reads are open; writes carry X-Kiosk-Secret from device storage.
const KioskPage = lazy(() => import('./modules/operations/trailers/pages/KioskPage.jsx')
  .then((m) => ({ default: m.KioskPage })));
// Evacuation page mounts OUTSIDE AppShell so print CSS doesn't fight the
// sidebar/header. Auth still required (cookie-based session); the route just
// opts out of the shell. Phase 4.3c.
const EvacuationPage = lazy(() => import('./modules/workforce/attendance/pages/EvacuationPage.jsx')
  .then((m) => ({ default: m.EvacuationPage })));

/**
 * Returns the first route the user should land on after login.
 * Priority: Admin → Workforce → Operations → Client → Analytics.
 */
function firstAccessibleRoute(user) {
  if (!user) return '/login';
  if (user.global_role === 'admin') return '/admin';
  const roles = user.moduleRoles || {};
  for (const m of ['workforce', 'operations', 'client', 'analytics', 'admin']) {
    if (roles[m]) return `/${m}`;
  }
  return '/settings'; // no module access — at least show account info
}

function LoadingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-sky-100">
      <img src={logo} alt="Sincro" className="h-12 animate-pulse opacity-60" />
    </div>
  );
}

export default function App() {
  const { data: user, isLoading } = useCurrentUser();
  if (isLoading) return <LoadingScreen />;

  return (
    <Routes>
      {/* Kiosk — mounted OUTSIDE the auth wrapper + AppShell so the TC55
          renders chrome-free and needs no OAuth session. The building comes
          from the path, which is what lets one deployment serve every site;
          before v0.86.0 this took one Railway service per building because the
          building was compiled into the bundle.

          This route and its API namespace are the only unauthenticated
          surface in the app. Everything else falls through to the authed tree
          below, so a worker who types /admin gets the login wall. */}
      <Route
        path="/kiosk/:building/scan"
        element={
          <Suspense fallback={<LoadingScreen />}>
            <KioskPage />
          </Suspense>
        }
      />

      {/* Retired kiosk path. The legacy per-building services still serve this
          from their own (pinned) bundles during the transition window; on the
          main app it is just the admin view. */}
      <Route path="/operations/trailers/scan" element={<Navigate to="/operations/trailers" replace />} />

      {/* Evacuation roster — mounted outside AppShell. Requires session like the
          rest of the app; just opts out of the chrome so print CSS works cleanly. */}
      <Route
        path="/workforce/attendance/evacuation"
        element={
          user ? (
            <Suspense fallback={<LoadingScreen />}>
              <EvacuationPage />
            </Suspense>
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      <Route
        path="/login"
        element={user ? <Navigate to={firstAccessibleRoute(user)} replace /> : <Login />}
      />

      {/* Everything else requires auth; wrap in AppShell */}
      {user ? (
        <Route element={<AppShell />}>
          <Route path="/" element={<Navigate to={firstAccessibleRoute(user)} replace />} />
          <Route
            path="/workforce/*"
            element={
              <Suspense fallback={<LoadingScreen />}>
                <WorkforceRoutes />
              </Suspense>
            }
          />
          <Route
            path="/operations/*"
            element={
              <Suspense fallback={<LoadingScreen />}>
                <OperationsRoutes />
              </Suspense>
            }
          />
          <Route
            path="/client/*"
            element={
              <Suspense fallback={<LoadingScreen />}>
                <ClientRoutes />
              </Suspense>
            }
          />
          <Route
            path="/analytics/*"
            element={
              <Suspense fallback={<LoadingScreen />}>
                <AnalyticsRoutes />
              </Suspense>
            }
          />
          <Route
            path="/admin/*"
            element={
              <Suspense fallback={<LoadingScreen />}>
                <AdminRoutes />
              </Suspense>
            }
          />
          <Route path="/settings" element={<Settings />} />
          <Route path="*" element={<Navigate to={firstAccessibleRoute(user)} replace />} />
        </Route>
      ) : (
        <Route path="*" element={<Navigate to="/login" replace />} />
      )}
    </Routes>
  );
}
